/**
 * Doserne — Admin "Important" panel.
 *
 * Break-glass surface for the rare case where an anonymous report
 * contains threats, harassment, or other content that requires
 * knowing the author. Every reveal is audited and the audit log is
 * shown right below the reveal list so any admin can see who
 * looked at what and why.
 *
 * Not linked from the main menu. Reachable only from a small
 * "Important" link inside the Profile page (admin-only).
 */
import React, { useEffect, useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, ShieldAlert, EyeOff, Eye, ArrowLeft, ClipboardList, AlertTriangle,
  Clock, User as UserIcon,
} from "lucide-react";
import { useDoserneAuth } from "./doserneAuth";
import { useLang } from "./i18n";
import DoserneShell from "./DoserneShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DICT = {
  ro: {
    title: "Important — Sesizări anonime",
    subtitle: "Panou break-glass. Fiecare dezvăluire este înregistrată în audit.",
    back: "Înapoi la profil",
    warning: "Folosește doar în cazuri extreme (amenințări, hărțuire, discriminare). Utilizatorii au fost avertizați că anonimatul poate fi ridicat pentru astfel de situații.",
    anon_reports: "Sesizări anonime",
    empty: "Nu există sesizări anonime.",
    reveal_btn: "Dezvăluie autorul",
    reveal_dialog_title: "Confirmă dezvăluirea",
    reveal_dialog_reason: "Motiv (obligatoriu, înregistrat în audit)",
    reveal_dialog_placeholder: "Ex: Conține amenințări explicite; verific dacă e nevoie de HR sau poliție.",
    reveal_dialog_confirm: "Dezvăluie",
    reveal_dialog_cancel: "Anulează",
    revealed_author: "Autor",
    audit_title: "Audit dezvăluiri",
    audit_empty: "Niciun audit încă.",
    audit_by: "Dezvăluit de",
    audit_reason: "Motiv",
    audit_time: "Data",
    reveal_success: "Autor dezvăluit. Consultă cu discreție.",
    reveal_fail: "Nu am putut dezvălui.",
  },
  no: {
    title: "Viktig — Anonyme rapporter",
    subtitle: "Break-glass-panel. Hver avsløring loggføres i revisjonsloggen.",
    back: "Tilbake til profil",
    warning: "Bruk kun i alvorlige tilfeller (trusler, trakassering, diskriminering). Brukere er varslet om at anonymitet kan brytes i slike situasjoner.",
    anon_reports: "Anonyme rapporter",
    empty: "Ingen anonyme rapporter.",
    reveal_btn: "Avslør forfatter",
    reveal_dialog_title: "Bekreft avsløring",
    reveal_dialog_reason: "Årsak (obligatorisk, loggføres)",
    reveal_dialog_placeholder: "Ex: Inneholder eksplisitte trusler; vurderer HR / politi.",
    reveal_dialog_confirm: "Avslør",
    reveal_dialog_cancel: "Avbryt",
    revealed_author: "Forfatter",
    audit_title: "Revisjonslogg",
    audit_empty: "Ingen revisjonsposter ennå.",
    audit_by: "Avslørt av",
    audit_reason: "Årsak",
    audit_time: "Tidspunkt",
    reveal_success: "Forfatter avslørt. Behandle diskret.",
    reveal_fail: "Kunne ikke avsløre.",
  },
  en: {
    title: "Important — Anonymous reports",
    subtitle: "Break-glass panel. Every reveal is written to the audit log.",
    back: "Back to profile",
    warning: "Only for serious cases (threats, harassment, discrimination). Users have been told that anonymity may be lifted in such cases.",
    anon_reports: "Anonymous reports",
    empty: "No anonymous reports.",
    reveal_btn: "Reveal author",
    reveal_dialog_title: "Confirm reveal",
    reveal_dialog_reason: "Reason (required, written to audit)",
    reveal_dialog_placeholder: "e.g. Contains explicit threats; assessing HR / police involvement.",
    reveal_dialog_confirm: "Reveal",
    reveal_dialog_cancel: "Cancel",
    revealed_author: "Author",
    audit_title: "Reveal audit log",
    audit_empty: "No audit entries yet.",
    audit_by: "Revealed by",
    audit_reason: "Reason",
    audit_time: "When",
    reveal_success: "Author revealed. Handle with discretion.",
    reveal_fail: "Could not reveal.",
  },
};

function fmtDate(iso, lang) {
  if (!iso) return "";
  try {
    const locale = lang === "ro" ? "ro-RO" : lang === "en" ? "en-GB" : "no-NO";
    return new Date(iso).toLocaleString(locale, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function DoserneImportant() {
  const { user, loading, authHeader } = useDoserneAuth();
  const { lang } = useLang();
  const t = useMemo(() => DICT[lang] || DICT.no, [lang]);
  const [reports, setReports] = useState([]);
  const [audit, setAudit] = useState([]);
  const [busy, setBusy] = useState(false);
  const [revealDialog, setRevealDialog] = useState(null); // { reportId, subject }
  const [reason, setReason] = useState("");
  // Map: reportId → {author_email, author_name}. Populated after reveal.
  const [revealed, setRevealed] = useState({});

  const isAdmin = !!(user?.is_admin || user?.role === "platform_admin" || user?.is_doserne_admin);

  const load = async () => {
    setBusy(true);
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/doserne/reports/admin/anonymous`, authHeader()),
        axios.get(`${API}/doserne/reports/admin/reveal-audit`, authHeader()),
      ]);
      setReports(r1.data?.reports || []);
      setAudit(r2.data?.audit || []);
    } catch { /* silent */ }
    setBusy(false);
  };
  useEffect(() => { if (user && isAdmin) load(); }, [user, isAdmin]);

  const confirmReveal = async () => {
    if (!revealDialog || reason.trim().length < 3) return;
    try {
      const { data } = await axios.post(
        `${API}/doserne/reports/admin/reveal/${revealDialog.reportId}`,
        { reason: reason.trim() },
        authHeader(),
      );
      setRevealed((prev) => ({ ...prev, [revealDialog.reportId]: { author_email: data.author_email, author_name: data.author_name } }));
      toast.success(t.reveal_success);
      setRevealDialog(null);
      setReason("");
      load(); // refresh audit table
    } catch {
      toast.error(t.reveal_fail);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#fafaf7]"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  if (!user) return <Navigate to="/doserne/logg-inn" replace />;
  if (!isAdmin) return <Navigate to="/doserne" replace />;

  return (
    <DoserneShell title={t.title}>
      <Link to="/doserne/profil" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 mb-3">
        <ArrowLeft className="w-3.5 h-3.5" /> {t.back}
      </Link>

      <div className="bg-gradient-to-br from-rose-50 via-white to-amber-50 border-2 border-rose-200 rounded-2xl p-4 mb-5" data-testid="important-hero">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-rose-500 flex items-center justify-center flex-shrink-0" style={{ color: "#fff" }}>
            <ShieldAlert className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h1 className="text-lg font-black text-slate-900">{t.title}</h1>
            <p className="text-sm text-slate-700 mt-0.5">{t.subtitle}</p>
            <div className="mt-3 flex items-start gap-2 p-2 rounded-lg bg-amber-100/60 border border-amber-200">
              <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800 leading-relaxed">{t.warning}</p>
            </div>
          </div>
        </div>
      </div>

      <section className="mb-6">
        <h2 className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2 inline-flex items-center gap-1.5">
          <EyeOff className="w-3.5 h-3.5" /> {t.anon_reports} · {reports.length}
        </h2>
        {busy && reports.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : reports.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-4 text-sm text-slate-500">{t.empty}</div>
        ) : (
          <ul className="space-y-2">
            {reports.map((r) => {
              const rev = revealed[r.id];
              return (
                <li key={r.id} className="bg-white border border-slate-200 rounded-xl p-3" data-testid={`important-report-${r.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-black text-slate-900">{r.subject}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">{fmtDate(r.created_at, lang)} · {r.category}</div>
                      <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap">{r.body}</p>
                      {rev && (
                        <div className="mt-3 p-2.5 rounded-lg bg-rose-50 border border-rose-200 flex items-center gap-2" data-testid={`important-revealed-${r.id}`}>
                          <UserIcon className="w-4 h-4 text-rose-700" />
                          <div className="text-sm">
                            <div className="font-black text-rose-900">{t.revealed_author}: {rev.author_name || "—"}</div>
                            <div className="text-[11px] text-rose-700">{rev.author_email}</div>
                          </div>
                        </div>
                      )}
                    </div>
                    {!rev && (
                      <button
                        type="button"
                        onClick={() => { setRevealDialog({ reportId: r.id, subject: r.subject }); setReason(""); }}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-rose-500 hover:bg-rose-600 text-xs font-black shadow-sm flex-shrink-0"
                        style={{ color: "#fff" }}
                        data-testid={`important-reveal-${r.id}`}
                      >
                        <Eye className="w-3.5 h-3.5" /> {t.reveal_btn}
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section>
        <h2 className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2 inline-flex items-center gap-1.5">
          <ClipboardList className="w-3.5 h-3.5" /> {t.audit_title} · {audit.length}
        </h2>
        {audit.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-4 text-sm text-slate-500">{t.audit_empty}</div>
        ) : (
          <ul className="space-y-1.5">
            {audit.map((a) => (
              <li key={a.id} className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs" data-testid={`important-audit-${a.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-black text-slate-900 truncate">{t.audit_by}: {a.revealed_by_name}</span>
                  <span className="inline-flex items-center gap-1 text-slate-500 flex-shrink-0">
                    <Clock className="w-3 h-3" /> {fmtDate(a.revealed_at, lang)}
                  </span>
                </div>
                <div className="text-slate-600 mt-1"><span className="font-black">{t.audit_reason}:</span> {a.reason}</div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Reveal confirmation dialog */}
      {revealDialog && (
        <div className="fixed inset-0 z-[90] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setRevealDialog(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="reveal-dialog">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-rose-500 flex items-center justify-center flex-shrink-0" style={{ color: "#fff" }}>
                <ShieldAlert className="w-5 h-5" />
              </div>
              <div>
                <div className="text-base font-black text-slate-900">{t.reveal_dialog_title}</div>
                <div className="text-xs text-slate-500 mt-0.5 truncate max-w-[24rem]">&ldquo;{revealDialog.subject}&rdquo;</div>
              </div>
            </div>
            <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">{t.reveal_dialog_reason}</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              maxLength={500}
              autoFocus
              placeholder={t.reveal_dialog_placeholder}
              className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
              data-testid="reveal-reason"
            />
            <div className="flex items-center justify-end gap-2 mt-3">
              <button
                type="button"
                onClick={() => { setRevealDialog(null); setReason(""); }}
                className="px-3 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100"
                data-testid="reveal-cancel"
              >
                {t.reveal_dialog_cancel}
              </button>
              <button
                type="button"
                onClick={confirmReveal}
                disabled={reason.trim().length < 3}
                className="px-3 py-2 rounded-lg text-sm font-black bg-rose-500 hover:bg-rose-600 disabled:bg-slate-300"
                style={{ color: "#fff" }}
                data-testid="reveal-confirm"
              >
                {t.reveal_dialog_confirm}
              </button>
            </div>
          </div>
        </div>
      )}
    </DoserneShell>
  );
}
